// ─────────────────────────────────────────────────────────────────────────────
// reportsShared.jsx
// Small shared helpers + a StatCard used by School Reports and Student Reports.
// Visual primitives (Card, Badge, tables, tokens) are reused from the fee
// module's design kit (../fees/feeUI) so the whole app stays on one theme.
// ─────────────────────────────────────────────────────────────────────────────
import React from "react";
import { C } from "../fees/feeUI";

// Logged-in admin's assigned campus — re-exported so report pages can import it
// alongside the other report helpers. Source of truth lives in utils.
export { useAdminCampus } from "../../../utils/useAdminCampus";

// Backend StudentResponse uses class_name/class_section/student_id/student_status.
// Adapt once at the boundary so the report UIs can work in plain terms.
export const normalizeStudents = (students = []) =>
  students.map(s => ({
    ...s,
    class:   s.class_name || s.class || "",
    section: s.class_section || s.section || "",
    rollNo:  s.student_id || s.rollNo || s.id,
    status:  s.student_status || s.status || "Active",
  }));

export const isActive = (s) => (s.status || s.student_status) === "Active";

// Status → Badge colour (see feeUI Badge palette)
export const statusColor = (status) => ({
  Active:    "green",
  Inactive:  "slate",
  Withdrawn: "red",
  Graduated: "blue",
}[status] || "slate");

// ── localStorage fee data (same stores the Fee module writes) ─────────────────
export const loadFeeAssignments = () => {
  try { return JSON.parse(localStorage.getItem("fee_assignments_v2") || "{}") || {}; }
  catch { return {}; }
};
export const loadPayments = () => {
  try { const p = JSON.parse(localStorage.getItem("payment_history") || "[]"); return Array.isArray(p) ? p : []; }
  catch { return []; }
};
export const loadPrevDues = (sid) => {
  try {
    return JSON.parse(localStorage.getItem(`previous_dues_${sid}`) || "[]")
      .filter(d => !d.paid).reduce((s, d) => s + (d.amount || 0), 0);
  } catch { return 0; }
};

export const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-PK", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// ── Stat card (accent top-rule, big value) ────────────────────────────────────
export const StatCard = ({ label, value, sub, accent = C.navy }) => (
  <div style={{
    background: C.white, border: `1px solid ${C.border}`, borderTop: `3px solid ${accent}`,
    borderRadius: "10px", padding: "16px 18px", boxShadow: "0 1px 3px rgba(15,29,54,0.05)",
  }}>
    <div style={{ fontSize: "10.5px", fontWeight: 700, color: C.slate, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "8px" }}>{label}</div>
    <div style={{ fontSize: "24px", fontWeight: 800, color: C.text, lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: "11px", color: C.slateLight, marginTop: "4px" }}>{sub}</div>}
  </div>
);

export const StatGrid = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: "16px", marginBottom: "20px" }}>
    {children}
  </div>
);

// Page heading used by both report pages. Optional `actions` render on the right.
export const PageHeading = ({ title, subtitle, actions }) => (
  <div style={{ marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: "16px", flexWrap: "wrap" }}>
    <div>
      <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "24px", fontWeight: 700, color: C.text, margin: 0 }}>{title}</h1>
      <p style={{ fontSize: "13px", color: C.slate, marginTop: "4px" }}>{subtitle}</p>
    </div>
    {actions}
  </div>
);

// Export CSV + Print Report buttons, shown on every report page.
export const ReportToolbar = ({ onExport, onPrint }) => {
  const base = { display: "inline-flex", alignItems: "center", gap: "7px", padding: "9px 16px", borderRadius: "8px", fontSize: "12.5px", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" };
  return (
    <div style={{ display: "flex", gap: "8px" }}>
      <button onClick={onExport} style={{ ...base, background: C.white, color: C.text, border: `1px solid ${C.border}` }}>
        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 3v12m0 0l-4-4m4 4l4-4"/><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/></svg>
        Export CSV
      </button>
      <button onClick={onPrint} style={{ ...base, background: "#1d4ed8", color: "#fff", border: "none" }}>
        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 9V3h12v6"/><rect x="6" y="14" width="12" height="7" rx="1"/><path d="M6 18H4a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-2"/></svg>
        Print Report
      </button>
    </div>
  );
};

// Shared table pagination footer (parent owns page state + row slicing)
export const Pagination = ({ page, perPage, total, onChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  if (totalPages <= 1) return null;
  const safe = Math.min(page, totalPages);
  const navBtn = (disabled) => ({
    padding: "5px 12px", fontSize: "12px", fontWeight: 600, borderRadius: "6px",
    border: `1px solid ${C.border}`, background: C.white, color: C.slate,
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
  });
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderTop: `1px solid ${C.border}`, flexWrap: "wrap", gap: "10px" }}>
      <span style={{ fontSize: "12px", color: C.slate }}>
        Showing {(safe - 1) * perPage + 1}–{Math.min(safe * perPage, total)} of {total}
      </span>
      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
        <button onClick={() => onChange(safe - 1)} disabled={safe === 1} style={navBtn(safe === 1)}>← Prev</button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter(p => p === 1 || p === totalPages || Math.abs(p - safe) <= 1)
          .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push("…"); acc.push(p); return acc; }, [])
          .map((p, i) => p === "…"
            ? <span key={`e${i}`} style={{ padding: "5px 4px", fontSize: "12px", color: C.slateLight }}>…</span>
            : <button key={p} onClick={() => onChange(p)} style={{ padding: "5px 10px", fontSize: "12px", fontWeight: 600, borderRadius: "6px", border: p === safe ? "none" : `1px solid ${C.border}`, background: p === safe ? C.navy : C.white, color: p === safe ? "#fff" : C.text, cursor: "pointer" }}>{p}</button>
          )}
        <button onClick={() => onChange(safe + 1)} disabled={safe === totalPages} style={navBtn(safe === totalPages)}>Next →</button>
      </div>
    </div>
  );
};

// Back button (returns from a detail view to the list)
export const BackButton = ({ onClick, label = "Back" }) => (
  <button onClick={onClick} style={{
    display: "inline-flex", alignItems: "center", gap: "7px", background: C.white,
    border: `1px solid ${C.border}`, borderRadius: "8px", padding: "8px 14px",
    fontSize: "13px", fontWeight: 600, color: C.slate, cursor: "pointer", marginBottom: "16px",
  }}
  onMouseEnter={e => { e.currentTarget.style.background = C.bg; }}
  onMouseLeave={e => { e.currentTarget.style.background = C.white; }}>
    <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
    {label}
  </button>
);
